/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import LoadingPlaceholder from '../../components/LoaderContainer/LoadingPlaceholder';
import SurveyQuestionList from '../../components/survey-question-list/survey-question-list';
import { useAppSelector } from '../../reducers';
import Dashboard, { DashboardRow, DashboardSection, DashboardSectionTitle } from '../../components/dashboard';
import { useParams } from 'react-router';
import { AdminBreadcrumbs } from './AdminBreadcrumbs';
import Loader from '../../components/loader';
import { generateUrl } from '../util';
import { ROUTES } from '../../constants/routes';
import { CompleteButton } from '../../components/survey/button/CompleteButton';
import { getPeriodName, getPeriodRange } from '../../utils/universalTracker';
import { ProgressRow } from './ProgressRow';
import { toPercentageProgress } from './util';
import { DataPeriods } from '../../types/universalTracker';
import { DATE, formatDate } from '../../utils/date';
import { SurveyMessageModal } from '../../components/message-modal/MessageModal';
import { getCurrentUser, isUserManagerByInitiativeId } from '../../selectors/user';
import { SurveyQuestionListToolbar } from '../../components/survey/survey-question-list-toolbar';
import { ScopeViewDropdown } from '../../components/survey/survey-question-list-toolbar/partials/ScopeViewDropdown';
import { Spacer } from '../../components/survey/survey-question-list-toolbar/partials/Spacer';
import { DelegationViewToggle } from '../../components/survey/survey-question-list-toolbar/partials/DelegationViewToggle';
import { getSurveyQuestionProgress, useSurveyGroups } from '../../utils/survey';
import { ExpandAllToggle } from '../../components/survey/survey-question-list-toolbar/partials/ExpandAllToggle';
import { useGetUserSurveyDetailsQuery } from '../../api/initiative-stats';
import { ColumnTitle } from '../../components/survey-question-list/partials/ColumnTitle';
import { ColumnValue } from '../../components/survey-question-list/partials/ColumnValue';
import { Divider } from '../../components/tracking-list';
import { ColumnStatusIcon } from '../../components/survey-question-list/partials/ColumnStatusIcon';
import { ColumnContributors } from '../../components/survey-question-list/partials/ColumnContributors';
import { ColumnVerifiers } from '../../components/survey-question-list/partials/ColumnVerifiers';
import G17Client from '../../services/G17Client';
import { UserMin } from '../../constants/users';
import { BulkActionToolbar } from '../../components/survey-question-list/partials/BulkActionToolbar';
import { Link } from 'react-router-dom';
import { ColumnOptionsMenu } from '../../components/survey-question-list/partials/ColumnOptionsMenu';
import SimpleTooltip from '../../components/simple-tooltip';
import { SearchQuestions } from '../../components/survey/survey-question-list-toolbar/partials/SearchQuestions';
import { useFilterQuestions } from './hooks/useFilterQuestions';
import { FilterQuestionsToggle } from '../../components/survey/survey-question-list-toolbar/partials/FilterQuestionsToggle';
import { useGetInitiativeByIdQuery } from '../../api/initiatives';
import { isRootOrg } from '../../selectors/initiative';
import { QUESTION } from '../../constants/terminology';
import { ColumnBookmark } from '../../components/survey-question-list/partials/ColumnBookmark';

export interface AdminSurveyParams {
  initiativeId: string;
  surveyId: string;
}

export const AdminSurveyRoute = () => {

  const { initiativeId, surveyId } = useParams<AdminSurveyParams>();

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [users, setUsers] = useState<undefined | UserMin[]>();

  const [delegationView, setDelegationView] = useState(true);

  const currentUser = useAppSelector(getCurrentUser);
  const isUserAdmin = useAppSelector((state) => isUserManagerByInitiativeId(state, initiativeId));

  const surveyDetails = useGetUserSurveyDetailsQuery({
    initiativeId,
    surveyId,
    userId: currentUser?._id ?? ''
  }, {
    skip: !surveyId || !currentUser
  });

  const isSurveyLoaded = surveyDetails.isSuccess;
  const overviewMode = useAppSelector((state) => state.surveySettings.overviewMode);
  const isRootOrganization = useAppSelector(isRootOrg);

  const { data: initiative } = useGetInitiativeByIdQuery(surveyDetails.data?.initiativeId || '', { skip: !surveyDetails.data?.initiativeId });

  const surveyGroups = useSurveyGroups(overviewMode, surveyDetails.data);
  const progressStats = useMemo(() => getSurveyQuestionProgress(surveyGroups), [surveyGroups]);

  useEffect(() => {
    if (!surveyId || !initiativeId || users || !delegationView) {
      return;
    }
    G17Client.getStakeholdersStats({
      initiativeId,
      surveyIds: [surveyId],
    }).then(setUsers)
  }, [surveyId, initiativeId, users, delegationView]);

  const { filters, changeFilter, disabledUTRs } = useFilterQuestions({
    survey: surveyDetails.data,
    surveyGroups,
    materiality: initiative?.materiality,
  });

  if (!isSurveyLoaded) {
    return <Loader />;
  }

  const surveyData = surveyDetails.data;
  const name = surveyData.name ?? surveyData.initiatives?.[0]?.name ?? '-';

  const handleReload = async () => surveyDetails.refetch();

  function handleMessage() {
    setShowMessageModal(true);
  }

  const handleDownloadSurvey = async () => {
    if (!currentUser) {
      return;
    }
    return G17Client.downloadManagerSurveyStats(initiativeId, currentUser._id, surveyId);
  }

  const periodName = getPeriodName(surveyData.period, false);
  const periodRange = getPeriodRange(surveyData.period, surveyData.effectiveDate)

  const range = periodRange.period === DataPeriods.Monthly ? <></> : <span className={'pl-2 border-start'}>
    {formatDate(periodRange.from, DATE.MONTH_YEAR_SHORT)} - {formatDate(periodRange.to, DATE.MONTH_YEAR_SHORT)}
  </span>

  return <div className='admin-dashboard'>
    <Dashboard>
      <DashboardRow>
        <AdminBreadcrumbs
          initiativeId={initiativeId}
          breadcrumbs={[
            {
              label: 'Admin Dashboard',
              url: generateUrl(ROUTES.ADMIN_DASHBOARD, { initiativeId }),
            },
            {
              label: name,
            },
          ]}
        />
      </DashboardRow>
      <DashboardRow
        title={
          <span className='d-flex dashboard-row__title'>
            <SimpleTooltip text={name} className='text-truncate'>
              <span>Admin dashboard: {name}</span>
            </SimpleTooltip>
            <Button color='link' className='mx-3 px-0 border-0' onClick={() => handleDownloadSurvey()}>
              <i className='fas fa-file-excel' />
            </Button>
          </span>
        }
        className={'header-row mb-5'}
        buttons={[
          <Button color={'secondary'} outline onClick={handleMessage}>Message</Button>,
          <CompleteButton survey={surveyData} isUserAdmin={isUserAdmin} handleReload={handleReload} />,
        ]} >
        <div className='ml-2 header-subtitle'>
          <span className='pr-2'>{periodName}</span>
          <span className='pr-2'>{range}</span>
          <span><Link to={generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId })}>View survey</Link></span>
        </div>
      </DashboardRow>

      <ProgressRow title={name} progress={toPercentageProgress(progressStats)} />

      <DashboardSectionTitle title={QUESTION.CAPITALIZED_PLURAL} />

      <LoadingPlaceholder height={77} isLoading={!surveyGroups} className='mb-4'>
        <DashboardSection>

          <div className='mt-3'>
            <SurveyQuestionList
              surveyId={surveyData._id}
              initiativeId={surveyData.initiativeId}
              surveyGroups={surveyGroups}
              disabledUTRs={disabledUTRs}
              bulkActionToolbar={(props) => (
                <BulkActionToolbar
                  {...props}
                  surveyData={surveyData}
                  handleReload={handleReload}
                  hiddenOptions={isRootOrganization ? [] : ['decimal', 'tag']}
                />
              )}
              columns={
                delegationView ?
                  [
                    ColumnBookmark,
                    ColumnTitle,
                    Divider,
                    (props) => <ColumnContributors {...props} users={users} />,
                    (props) => <ColumnVerifiers {...props} users={users} />,
                    Divider,
                    ColumnStatusIcon,
                    ColumnOptionsMenu,
                  ]
                  :
                  [
                    ColumnBookmark,
                    ColumnTitle,
                    Divider,
                    ColumnValue,
                    Divider,
                    ColumnStatusIcon,
                    ColumnOptionsMenu,
                  ]
              }
              toolbar={(props) => (
                <SurveyQuestionListToolbar
                  {...props}
                  settings={filters}
                  handleChangeSettings={changeFilter}
                  surveyScope={surveyData.scope}
                  components={[
                    ScopeViewDropdown,
                    SearchQuestions,
                    FilterQuestionsToggle,
                    Spacer,
                    (props) => <DelegationViewToggle {...props} handleCheckbox={setDelegationView} delegationView={delegationView} />,
                    ExpandAllToggle,
                  ]}
                />

              )}
            />
          </div>
        </DashboardSection>
      </LoadingPlaceholder>

    </Dashboard>
    {showMessageModal ? <SurveyMessageModal surveyId={surveyId} toggle={() => setShowMessageModal(!showMessageModal)} /> : null}
  </div>
}

